import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { toast } from 'sonner';
import { PageHead } from '@/admin-area/ui/PageHead';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { listPeople, type Person } from '../capture/api';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';
import { listReservedPurposes, setReservedPeople, type ReservedPurpose } from './api';

type Loaded =
  | { readonly status: 'loading' | 'failed' }
  | {
      readonly status: 'ready';
      readonly purposes: readonly ReservedPurpose[];
      readonly people: readonly Person[];
    };

/**
 * «Motivazioni riservate»: Gestione chooses, among the organisation's people, who may use each
 * restricted purpose (M17, M18). Until someone is chosen, nobody uses it. Audited by the server.
 */
export function ReservedPurposesPage() {
  const permissions = useExpensesPermissions();
  if (permissions.status === 'loading') return null;
  if (!holds(permissions, EXPENSES_MANAGE)) return <Navigate to="/expenses" replace />;
  return <ReservedPurposes />;
}

function ReservedPurposes() {
  const { t } = useTranslation('expenses');
  const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' });
  // Bumped by «Riprova»: a failed read (the purposes or the people list) is asked again.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    void Promise.all([listReservedPurposes(), listPeople()]).then(([purposes, people]) => {
      if (!live) return;
      setLoaded(
        purposes.ok && people.ok
          ? { status: 'ready', purposes: purposes.value, people: people.value }
          : { status: 'failed' },
      );
    });
    return () => {
      live = false;
    };
  }, [attempt]);

  return (
    <section aria-label={t('reserved.title')} className="flex max-w-xl flex-col gap-6">
      <PageHead title={t('reserved.title')} summary={t('reserved.summary')} />
      {loaded.status === 'loading' ? <p role="status">{t('reserved.loading')}</p> : null}
      {loaded.status === 'failed' ? (
        <div className="flex flex-col items-start gap-2">
          <p role="alert">{t('reserved.loadFailed')}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setLoaded({ status: 'loading' });
              setAttempt((n) => n + 1);
            }}
          >
            {t('reserved.retry')}
          </Button>
        </div>
      ) : null}
      {loaded.status === 'ready' && loaded.purposes.length === 0 ? (
        <p>{t('reserved.none')}</p>
      ) : null}
      {loaded.status === 'ready'
        ? loaded.purposes.map((purpose) => (
            <PurposePicker key={purpose.code} purpose={purpose} people={loaded.people} />
          ))
        : null}
    </section>
  );
}

function PurposePicker({
  purpose,
  people,
}: {
  readonly purpose: ReservedPurpose;
  readonly people: readonly Person[];
}) {
  const { t } = useTranslation('expenses');
  const uid = useId();
  const [saved, setSaved] = useState(purpose);
  const [chosen, setChosen] = useState<ReadonlySet<string>>(
    new Set(purpose.people.map((person) => person.userId)),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // A second submit before React re-renders the disabled button is refused here, not sent twice.
  const inFlight = useRef(false);

  // The organisation's people, then those still named but no longer listed (so they can be removed).
  const gone = saved.people.filter((person) => !person.active);
  const options = [
    ...people.map((person) => ({ userId: person.userId, name: person.displayName, active: true })),
    ...gone.map((person) => ({ userId: person.userId, name: '', active: false })),
  ];
  const toggle = (userId: string): void => {
    const next = new Set(chosen);
    if (!next.delete(userId)) next.add(userId);
    setChosen(next);
  };

  const save = async (): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setError(null);
    const outcome = await setReservedPeople(saved.code, [...chosen]);
    inFlight.current = false;
    setSaving(false);
    if (outcome.ok) {
      setSaved(outcome.value);
      setChosen(new Set(outcome.value.people.map((person) => person.userId)));
      toast.success(t('reserved.saved', { code: saved.code }));
    } else {
      setError(t(`reserved.problems.${outcome.code}`));
    }
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <fieldset className="flex flex-col gap-2" aria-describedby={`${uid}-hint`}>
        <legend className="text-sm font-semibold">
          {saved.code} · {saved.label}
        </legend>
        <p id={`${uid}-hint`} className="text-muted-foreground text-sm">
          {chosen.size === 0 ? t('reserved.nobody') : t('reserved.hint')}
        </p>
        {options.map((option) => (
          <label key={option.userId} className="flex items-center gap-2 text-sm">
            <Checkbox checked={chosen.has(option.userId)} onChange={() => toggle(option.userId)} />
            {option.active ? option.name : t('reserved.inactive')}
          </label>
        ))}
      </fieldset>
      {error === null ? null : (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      )}
      <div>
        <Button
          type="submit"
          disabled={saving}
          aria-busy={saving}
          aria-label={t('reserved.saveFor', { code: saved.code })}
        >
          {saving ? t('reserved.saving') : t('reserved.save')}
        </Button>
      </div>
    </form>
  );
}
